import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePresence } from '../context/PresenceContext';
import api from '../services/api';
import {
  Video,
  MessageSquare,
  Hash,
  Lock,
  Plus,
  Users,
  MessageCircle,
  Sparkles,
  Loader2,
  User,
  Bell,
  BellOff,
  Briefcase
} from 'lucide-react';
import TextMessages from '../components/sigil/TextMessages';
import CreateChannelModal from '../components/sigil/CreateChannelModal';
import ChannelSettingsModal from '../components/sigil/ChannelSettingsModal';
import SpaceBackground from '../components/layout/SpaceBackground';
import MessageList from '../components/sigil/MessageList';
import MessageInput from '../components/sigil/MessageInput';

const Sigil = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // ?thread= deep-links (callback items, push notifications) land on texts.
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get('thread') ? 'texts' : 'chat');
  const { typingUsers, sendTypingIndicator, markChannelRead, refreshUnread } = usePresence();
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [showChannelSettings, setShowChannelSettings] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const typingTimeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  // Astraea AI DM state
  const [isAstraeaAISelected, setIsAstraeaAISelected] = useState(false);
  const [aiMessages, setAiMessages] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSending, setAiSending] = useState(false);

  // Notification permission state
  const [notificationPermission, setNotificationPermission] = useState(
    typeof Notification !== 'undefined' ? Notification.permission : 'denied'
  );

  const requestNotificationPermission = async () => {
    if (typeof Notification === 'undefined') {
      alert('Notifications not supported in this browser');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);

      if (permission === 'granted') {
        new Notification('Notifications Enabled', {
          body: 'You will now receive alerts for new intel updates',
          icon: '/favicon.ico'
        });
      } else if (permission === 'denied') {
        alert('Notifications blocked. Check your browser settings to enable.');
      }
    } catch (err) {
      console.error('Notification error:', err);
      alert('Could not request notification permission: ' + err.message);
    }
  };

  // Load channels
  useEffect(() => {
    loadChannels();
  }, []);

  // Load messages when channel changes and setup polling
  useEffect(() => {
    if (selectedChannel) {
      loadMessages(selectedChannel.id, true);
      markChannelRead(selectedChannel.id);

      // Poll for new messages every 10 seconds while channel is selected
      pollIntervalRef.current = setInterval(() => {
        checkIfNearBottom();
        loadMessages(selectedChannel.id);
      }, 10000);

      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
        }
      };
    }
  }, [selectedChannel, markChannelRead]);

  // Check scroll position before messages update
  const checkIfNearBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      isNearBottomRef.current = scrollHeight - scrollTop - clientHeight < 150;
    }
  }, []);

  // Auto-scroll to bottom when new messages arrive (only if user was near bottom)
  useEffect(() => {
    const isNewMessage = messages.length > prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    if (isNewMessage && isNearBottomRef.current) {
      scrollToBottom();
    }

    // Show browser notification for new intel messages (except our own)
    if (isNewMessage && messages.length > 0 && selectedChannel?.name === 'intel') {
      const latestMsg = messages[messages.length - 1];
      // Don't notify about our own messages
      if (latestMsg && latestMsg.user_id !== user?.id && Notification.permission === 'granted') {
        new Notification('Nassau Intel Update', {
          body: 'New political intel digest available',
          icon: '/favicon.ico'
        });
      }
    }
  }, [messages, selectedChannel, user]);


  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChannels = async () => {
    try {
      const response = await api.get('/sigil/channels');
      setChannels(response.data);

      // Select first channel if none selected
      if (!selectedChannel && response.data.length > 0) {
        setSelectedChannel(response.data[0]);
      }
    } catch (error) {
      console.error('Error loading channels:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (channelId, markRead) => {
    try {
      const response = await api.get(`/sigil/channels/${channelId}/messages${markRead ? '?mark_read=1' : ''}`);
      setMessages(response.data.reverse()); // Reverse to show oldest first
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  };

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (!selectedChannel) return;

    // Send typing indicator
    sendTypingIndicator(selectedChannel.id, true);

    // Clear previous timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of no input
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingIndicator(selectedChannel.id, false);
    }, 2000);
  }, [selectedChannel, sendTypingIndicator]);

  const handleSendMessage = async (content, attachmentIds = []) => {
    if (!content.trim() && attachmentIds.length === 0) return;

    // Clear typing indicator
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    sendTypingIndicator(selectedChannel.id, false);

    setSendingMessage(true);
    try {
      const response = await api.post('/sigil/messages', {
        channel_id: selectedChannel.id,
        content: content.trim(),
        attachment_ids: attachmentIds
      });

      setMessages([...messages, response.data]);

      // Refresh channels to update unread count
      loadChannels();
      refreshUnread();
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleCreateChannel = async (channelData) => {
    try {
      await api.post('/sigil/channels', channelData);
      setShowCreateChannel(false);
      loadChannels();
    } catch (error) {
      console.error('Error creating channel:', error);
      alert('Failed to create channel');
    }
  };

  const handleReaction = async (messageId, emoji) => {
    try {
      await api.post(`/sigil/messages/${messageId}/reactions`, { emoji });
      loadMessages(selectedChannel.id);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await api.delete(`/sigil/messages/${messageId}`);
      loadMessages(selectedChannel.id);
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message');
    }
  };

  // Astraea AI functions
  const selectAstraeaAI = async () => {
    setSelectedChannel(null);
    setIsAstraeaAISelected(true);
    setAiLoading(true);

    try {
      const response = await api.get('/astraea-internal/conversations');
      if (response.data.length > 0) {
        const convResponse = await api.get(`/astraea-internal/conversations/${response.data[0].id}`);
        setAiMessages(convResponse.data.messages || []);
      } else {
        setAiMessages([]);
      }
    } catch (error) {
      console.error('Error loading AI conversation:', error);
      setAiMessages([]);
    } finally {
      setAiLoading(false);
    }
  };

  const handleSendToAI = async (content) => {
    if (!content.trim() || aiSending) return;

    // Optimistically add user message
    const tempUserMsg = { role: 'user', content: content.trim(), created_at: new Date().toISOString() };
    setAiMessages(prev => [...prev, tempUserMsg]);
    setAiSending(true);

    try {
      const response = await api.post('/astraea-internal/chat', { message: content.trim() });
      const aiMessage = { role: 'assistant', content: response.data.response, created_at: new Date().toISOString() };
      setAiMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Error sending to AI:', error);
      alert('Failed to send message to Astraea');
    } finally {
      setAiSending(false);
    }
  };

  const handleChannelSelect = (channel) => {
    setIsAstraeaAISelected(false);
    setSelectedChannel(channel);
  };

  const isClientChannel = (channel) => {
    return channel.name && channel.name.startsWith('client_');
  };

  const isCaseChannel = (channel) => {
    return channel.name && channel.name.startsWith('Case:');
  };

  const getChannelDisplayName = (channel) => {
    // For client channels, extract name from description
    if (isClientChannel(channel) && channel.description) {
      // Description format: "Messages from {Client Name}"
      const match = channel.description.match(/^Messages from (.+)$/);
      if (match) {
        return match[1];
      }
    }
    // For DM channels, transform the name
    if (channel.channel_type === 'direct') {
      return channel.name.replace('dm_', '').replace(/_/g, ' & ');
    }
    return channel.name;
  };

  const getChannelIcon = (channel) => {
    // Case channels get a briefcase icon
    if (isCaseChannel(channel)) {
      return <Briefcase size={18} className="text-amber-400" />;
    }
    // Client channels get a user icon
    if (isClientChannel(channel)) {
      return <User size={18} className="text-green-400" />;
    }
    switch (channel.channel_type) {
      case 'public':
        return <Hash size={18} className="text-cyan-400" />;
      case 'private':
        return <Lock size={18} className="text-blue-400" />;
      case 'direct':
        return <MessageSquare size={18} className="text-cyan-400" />;
      default:
        return <Hash size={18} className="text-cyan-400" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-900 via-slate-900/30 to-slate-900">
        <div className="flex items-center gap-3 text-cyan-300">
          <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  const viewTabs = (
    <div className="flex gap-1 bg-slate-900/60 rounded-lg p-1">
      {[['chat', 'Team chat'], ['texts', 'Clients']].map(([v, label]) => (
        <button
          key={v}
          onClick={() => setView(v)}
          className={`px-3 py-1 rounded-md text-xs font-medium transition ${
            view === v ? 'bg-cyan-500/30 text-white' : 'text-slate-400 hover:text-cyan-300'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (view === 'texts') {
    return (
      <div className="flex flex-col flex-1 min-h-0 rounded-xl overflow-hidden border border-cyan-500/20 relative">
        <SpaceBackground />
        <div className="flex items-center gap-3 p-3 border-b border-cyan-500/20 bg-slate-800/50 backdrop-blur relative z-10">
          <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/30">
            <MessageCircle size={16} className="text-white" />
          </div>
          <h2 className="text-lg font-semibold text-white">Sigil</h2>
          {viewTabs}
        </div>
        <div className="flex-1 min-h-0 relative z-10">
          <TextMessages />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-120px)] rounded-xl overflow-hidden border border-cyan-500/20 relative">
      <SpaceBackground />
      {/* Sidebar - Channel List */}
      <div className="w-72 bg-slate-800/50 backdrop-blur border-r border-cyan-500/20 flex flex-col relative z-10">
        {/* Header */}
        <div className="p-4 border-b border-cyan-500/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                <MessageCircle size={20} className="text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Sigil</h2>
                <p className="text-xs text-cyan-400">Team Messaging</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={requestNotificationPermission}
                className={`p-2 rounded-lg transition ${
                  notificationPermission === 'granted'
                    ? 'bg-green-500/20 text-green-300'
                    : 'bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 hover:text-white'
                }`}
                title={notificationPermission === 'granted' ? 'Notifications enabled' : 'Enable notifications'}
              >
                {notificationPermission === 'granted' ? <Bell size={20} /> : <BellOff size={20} />}
              </button>
              <button
                onClick={() => setShowCreateChannel(true)}
                className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg transition text-cyan-300 hover:text-white"
                title="Create Channel"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>
          <div className="mt-3">{viewTabs}</div>
        </div>

        {/* Channel List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-3">
            {/* Public Channels */}
            <div className="mb-6">
              <h3 className="px-3 text-xs font-semibold text-cyan-400 uppercase mb-2 tracking-wider">
                Channels
              </h3>
              {channels
                .filter(ch => ch.channel_type === 'public')
                .map(channel => (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelSelect(channel)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 transition-all ${
                      selectedChannel?.id === channel.id && !isAstraeaAISelected
                        ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-white border border-cyan-500/30'
                        : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {getChannelIcon(channel)}
                      <span className="text-sm font-medium truncate">
                        {channel.name}
                      </span>
                    </div>
                    {channel.unread_count > 0 && (
                      <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs rounded-full px-2 py-0.5 font-medium shadow-lg shadow-cyan-500/30">
                        {channel.unread_count}
                      </span>
                    )}
                  </button>
                ))}
            </div>

            {/* Case Channels */}
            {channels.filter(ch => isCaseChannel(ch)).length > 0 && (
              <div className="mb-6">
                <h3 className="px-3 text-xs font-semibold text-amber-400 uppercase mb-2 tracking-wider">
                  Cases
                </h3>
                {channels
                  .filter(ch => isCaseChannel(ch))
                  .map(channel => (
                    <button
                      key={channel.id}
                      onClick={() => handleChannelSelect(channel)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 transition-all ${
                        selectedChannel?.id === channel.id && !isAstraeaAISelected
                          ? 'bg-gradient-to-r from-amber-500/30 to-orange-500/30 text-white border border-amber-500/30'
                          : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {getChannelIcon(channel)}
                        <span className="text-sm font-medium truncate">
                          {channel.name.replace('Case: ', '')}
                        </span>
                      </div>
                      {channel.unread_count > 0 && (
                        <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs rounded-full px-2 py-0.5 font-medium">
                          {channel.unread_count}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            )}

            {/* Private Channels */}
            {channels.filter(ch => ch.channel_type === 'private' && !isClientChannel(ch) && !isCaseChannel(ch)).length > 0 && (
              <div className="mb-6">
                <h3 className="px-3 text-xs font-semibold text-cyan-400 uppercase mb-2 tracking-wider">
                  Private
                </h3>
                {channels
                  .filter(ch => ch.channel_type === 'private' && !isClientChannel(ch) && !isCaseChannel(ch))
                  .map(channel => (
                    <button
                      key={channel.id}
                      onClick={() => handleChannelSelect(channel)}
                      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 transition-all ${
                        selectedChannel?.id === channel.id && !isAstraeaAISelected
                          ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-white border border-cyan-500/30'
                          : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {getChannelIcon(channel)}
                        <span className="text-sm font-medium truncate">
                          {getChannelDisplayName(channel)}
                        </span>
                      </div>
                      {channel.unread_count > 0 && (
                        <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs rounded-full px-2 py-0.5 font-medium">
                          {channel.unread_count}
                        </span>
                      )}
                    </button>
                  ))}
              </div>
            )}

            {/* Direct Messages */}
            <div className="mb-6">
              <h3 className="px-3 text-xs font-semibold text-cyan-400 uppercase mb-2 tracking-wider">
                Direct Messages
              </h3>

              {/* Astraea AI - Staff/Admin only */}
              {(user?.role === 'admin' || user?.role === 'staff') && (
                <button
                  onClick={selectAstraeaAI}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg mb-1 transition-all ${
                    isAstraeaAISelected
                      ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-white border border-cyan-500/30'
                      : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                  }`}
                >
                  <Sparkles size={18} className="text-cyan-400" />
                  <span className="text-sm font-medium">Astraea AI</span>
                </button>
              )}

              {channels
                .filter(ch => ch.channel_type === 'direct')
                .map(channel => (
                  <button
                    key={channel.id}
                    onClick={() => handleChannelSelect(channel)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg mb-1 transition-all ${
                      selectedChannel?.id === channel.id && !isAstraeaAISelected
                        ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-white border border-cyan-500/30'
                        : 'hover:bg-slate-700/50 text-slate-300 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {getChannelIcon(channel)}
                      <span className="text-sm font-medium truncate">
                        {getChannelDisplayName(channel)}
                      </span>
                    </div>
                    {channel.unread_count > 0 && (
                      <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white text-xs rounded-full px-2 py-0.5 font-medium">
                        {channel.unread_count}
                      </span>
                    )}
                  </button>
                ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col bg-slate-900/50 relative z-10">
        {isAstraeaAISelected ? (
          <>
            {/* Astraea AI Header */}
            <div className="h-16 bg-slate-800/50 backdrop-blur border-b border-cyan-500/20 px-6 flex items-center">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
                  <Sparkles size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Astraea AI</h3>
                  <p className="text-sm text-cyan-400">Your firm's AI assistant</p>
                </div>
              </div>
            </div>

            {/* AI Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {aiLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
                </div>
              ) : aiMessages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Sparkles className="text-cyan-400 mb-4" size={48} />
                  <h2 className="text-xl font-bold text-white mb-2">
                    Welcome to Astraea AI
                  </h2>
                  <p className="text-slate-400 max-w-md">
                    Your firm's private AI assistant. All conversations are confidential.
                    Ask me anything about your cases, documents, or legal matters.
                  </p>
                </div>
              ) : (
                <div className="max-w-4xl mx-auto space-y-4">
                  {aiMessages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-3xl rounded-lg px-4 py-3 ${
                          message.role === 'user'
                            ? 'bg-cyan-600 text-white'
                            : 'bg-slate-800 border border-cyan-500/20 text-white'
                        }`}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center gap-2 mb-2">
                            <Sparkles size={16} className="text-cyan-400" />
                            <span className="font-semibold text-cyan-400">Astraea</span>
                          </div>
                        )}
                        <p className="whitespace-pre-wrap">{message.content}</p>
                      </div>
                    </div>
                  ))}

                  {aiSending && (
                    <div className="flex justify-start">
                      <div className="bg-slate-800 border border-cyan-500/20 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
                          <span className="text-slate-400">Astraea is thinking...</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* AI Message Input */}
            <div className="border-t border-cyan-500/20 p-4 bg-slate-800/30">
              <AIMessageInput onSend={handleSendToAI} disabled={aiSending} />
            </div>
          </>
        ) : selectedChannel ? (
          <>
            {/* Channel Header */}
            <div className={`h-16 bg-slate-800/50 backdrop-blur border-b px-6 flex items-center justify-between ${
              isCaseChannel(selectedChannel) ? 'border-amber-500/20' : isClientChannel(selectedChannel) ? 'border-green-500/20' : 'border-cyan-500/20'
            }`}>
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${
                  isCaseChannel(selectedChannel)
                    ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-500/30'
                    : isClientChannel(selectedChannel)
                    ? 'bg-gradient-to-br from-green-500/20 to-teal-500/20 border-green-500/30'
                    : 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-cyan-500/30'
                }`}>
                  {getChannelIcon(selectedChannel)}
                </div>
                <div>
                  <h3 className="font-semibold text-white">
                    {isCaseChannel(selectedChannel) ? selectedChannel.name.replace('Case: ', '') : getChannelDisplayName(selectedChannel)}
                  </h3>
                  {isCaseChannel(selectedChannel) ? (
                    <p className="text-sm text-amber-400">Case Communications</p>
                  ) : isClientChannel(selectedChannel) ? (
                    <p className="text-sm text-green-400">Client Messages</p>
                  ) : selectedChannel.description && (
                    <p className="text-sm text-slate-400">
                      {selectedChannel.description}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowChannelSettings(true)}
                  className="p-2 bg-slate-700/50 hover:bg-cyan-500/20 rounded-lg transition text-slate-400 hover:text-cyan-300"
                  title="Channel Settings"
                >
                  <Users size={20} />
                </button>
                <button
                  onClick={async () => {
                    try {
                      const res = await api.post(`/visage/channels/${selectedChannel.id}/start-meeting`);
                      navigate(`/visage?join=${res.data.id}`);
                    } catch (err) {
                      console.error('Error starting video meeting:', err);
                    }
                  }}
                  className="p-2 bg-slate-700/50 hover:bg-cyan-500/20 rounded-lg transition text-slate-400 hover:text-cyan-300"
                  title="Start Video Meeting"
                >
                  <Video size={20} />
                </button>
              </div>
            </div>

            {/* Messages */}
            <div
              ref={messagesContainerRef}
              onScroll={checkIfNearBottom}
              className="flex-1 overflow-y-auto p-6 space-y-4"
            >
              <MessageList
                messages={messages}
                currentUser={user}
                onReaction={handleReaction}
                onDelete={handleDeleteMessage}
              />
              <div ref={messagesEndRef} />
            </div>

            {/* Typing Indicator */}
            {selectedChannel && typingUsers[selectedChannel.id]?.length > 0 && (
              <div className="px-6 py-2 text-sm text-cyan-400 flex items-center gap-2">
                <span className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
                <span>Someone is typing...</span>
              </div>
            )}

            {/* Message Input */}
            <div className="border-t border-cyan-500/20 p-4 bg-slate-800/30">
              <MessageInput
                onSend={handleSendMessage}
                disabled={sendingMessage}
                onTyping={handleTyping}
                channelId={selectedChannel.id}
              />
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-cyan-500/30">
                <MessageSquare size={40} className="text-cyan-400" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Select a Channel</h3>
              <p className="text-slate-400">Choose a channel to start chatting with your team</p>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateChannel && (
        <CreateChannelModal
          onClose={() => setShowCreateChannel(false)}
          onCreate={handleCreateChannel}
        />
      )}

      {showChannelSettings && selectedChannel && (
        <ChannelSettingsModal
          channel={selectedChannel}
          onClose={() => setShowChannelSettings(false)}
          onUpdate={async () => {
            // Clear selection if the current channel was deleted
            const currentId = selectedChannel?.id;
            try {
              const response = await api.get('/sigil/channels');
              setChannels(response.data);

              // Check if the selected channel still exists
              const stillExists = response.data.some(ch => ch.id === currentId);
              if (!stillExists) {
                setSelectedChannel(response.data.length > 0 ? response.data[0] : null);
                setMessages([]);
                setShowChannelSettings(false);
              }
            } catch (error) {
              console.error('Error reloading channels:', error);
            }
          }}
        />
      )}
    </div>
  );
};

// Simple AI message input component
const AIMessageInput = ({ onSend, disabled }) => {
  const [message, setMessage] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSend(message);
      setMessage('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2">
      <input
        type="text"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Ask Astraea anything..."
        disabled={disabled}
        className="flex-1 bg-slate-800 border border-cyan-500/20 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-transparent"
      />
      <button
        type="submit"
        disabled={!message.trim() || disabled}
        className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-500/20"
      >
        Send
      </button>
    </form>
  );
};

export default Sigil;
